import type { OneSourceLineItem, AggregatedTaxLine } from '../types/tax.types';

/**
 * Aggregates One Source taxSummary entries across all line items by authorityType.
 *
 * @param lineItems        - Raw line items from the One Source API response.
 * @param quantityByLineRef - Optional map of lineReference → cart quantity.
 *                           One Source returns unit-level authorityTaxAmount, so we
 *                           multiply by quantity here to get the correct display total.
 *                           Defaults to 1 for any lineReference not present in the map.
 */
export function aggregateTaxLines(
  lineItems: OneSourceLineItem[] | null | undefined,
  quantityByLineRef: Record<number, number> = {}
): AggregatedTaxLine[] {
  if (!Array.isArray(lineItems) || lineItems.length === 0) {
    return [];
  }

  const accumulator = new Map<string, AggregatedTaxLine>();

  for (const lineItem of lineItems) {
    const taxSummary = lineItem?.priceData?.taxSummary;

    if (!Array.isArray(taxSummary) || taxSummary.length === 0) {
      continue;
    }

    // Quantity for this line item — OneSource returns per-unit tax amounts,
    // so we scale up by the cart quantity to get the correct display total.
    const qty = quantityByLineRef[lineItem.lineReference] ?? 1;

    for (const entry of taxSummary) {
      // Skip exempt entries — they do not contribute to displayed tax.
      if (entry.isExempt === true) {
        continue;
      }

      const key = entry.authorityType ?? '';
      if (!key) {
        continue;
      }

      // Scale the per-unit tax amount by the cart line item quantity.
      const scaledTaxAmount = (entry.authorityTaxAmount ?? 0) * qty;

      const existing = accumulator.get(key);

      if (existing) {
        existing.taxAmount += scaledTaxAmount;
        // Keep the first non-zero rate seen; subsequent entries for the same authority
        // should have the same rate (One Source guarantee), but guard defensively.
        if (existing.taxRate === 0 && entry.taxRate !== 0) {
          existing.taxRate = entry.taxRate;
        }
      } else {
        accumulator.set(key, {
          authorityType: key,
          taxRate: entry.taxRate ?? 0,
          taxAmount: scaledTaxAmount,
        });
      }
    }
  }

  if (accumulator.size === 0) {
    return [];
  }

  // Project to output shape and sort alphabetically for deterministic rendering.
  const result: AggregatedTaxLine[] = Array.from(accumulator.values())
    .sort((a, b) => a.authorityType.localeCompare(b.authorityType));

  return result;
}
